import React, { useState, useContext, useRef } from "react";
import { CurrentUserContext } from "App";
import { Form } from "react-bootstrap";
import FriendsDailyUpdateComponent from "./FriendsDailyUpdateComponent";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

export default function NewDailyUpdateInputArea({
  open,
  setOpen,
  setDailyPostElements,
  onPostCreated,
}) {
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const fileInputRef = useRef(null);

  const [newPostData, setNewPostData] = useState({
    created_by: currentUser.token,
    title: "",
    media_src: "",
    body: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPostData((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setImagePreview(base64);
      setNewPostData((prev) => ({ ...prev, media_src: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview(null);
    setNewPostData((prev) => ({ ...prev, media_src: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);
    fetch(`${API_URL}/newDailyUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ newPostData: newPostData }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          const {
            title: postTitle,
            media_src: postMedia,
            body: postBody,
            number_of_likes: numberOfLikes,
          } = r.newPost;
          setDailyPostElements((prev) => {
            return [
              <FriendsDailyUpdateComponent
                postBody={postBody}
                postTitle={postTitle}
                postMedia={postMedia}
                numberOfLikes={numberOfLikes}
              />,
              ...prev,
            ];
          });
          setOpen(!open);
          setImagePreview(null);
          // Notify parent of streak update
          if (r.streak && onPostCreated) {
            onPostCreated(r.streak);
          }
        }
      })
      .catch((err) => {
        console.error("Error creating post:", err);
        alert("Error creating post. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div
      id="daily-update-input"
      className="rounded-4 mb-5"
      style={{
        border: "2px solid var(--main-purple)",
        backgroundColor: "var(--nav-gray)",
        padding: "1.5rem 1rem",
        margin: "0 auto",
        maxWidth: "600px",
        width: "100%",
      }}
    >
      <div className="d-flex flex-column">
        <Form onSubmit={handleSubmit} className="d-flex flex-column">
          <Form.Group
            className="text-center"
            controlId="exampleForm.ControlTextarea"
            style={{ color: "var(--main-white)" }}
          >
            <Form.Control
              className="fw-bold text-center"
              as="textarea"
              name="title"
              value={newPostData.title}
              onChange={handleInputChange}
              rows={1}
              cols={100}
              placeholder="Daily Update"
              style={{
                backgroundColor: "transparent",
                color: "var(--main-white)",
                border: "none",
                resize: "none",
                fontSize: "1.6rem",
              }}
            />
            <Form.Control
              as="textarea"
              name="body"
              value={newPostData.body}
              onChange={handleInputChange}
              rows={5}
              cols={100}
              placeholder="Tell us what's up"
              style={{
                backgroundColor: "transparent",
                color: "var(--main-white)",
                border: "none",
                resize: "none",
              }}
            />
          </Form.Group>

          {/* Image preview */}
          {imagePreview && (
            <div
              className="mb-3 position-relative"
              style={{ maxWidth: "100%" }}
            >
              <img
                src={imagePreview}
                alt="Preview"
                className="rounded-3"
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  objectFit: "cover",
                }}
              />
              <button
                type="button"
                onClick={removeImage}
                className="btn btn-sm position-absolute"
                style={{
                  top: "8px",
                  right: "8px",
                  backgroundColor: "rgba(0,0,0,0.6)",
                  color: "white",
                  borderRadius: "50%",
                  width: "28px",
                  height: "28px",
                  padding: "0",
                  lineHeight: "28px",
                  fontSize: "1rem",
                }}
              >
                X
              </button>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: "none" }}
          />

          <div className="align-self-end d-flex align-items-center gap-2">
            <ion-icon
              name="image-outline"
              className="w-100"
              onClick={() => fileInputRef.current?.click()}
              style={{
                fontSize: "2.4rem",
                color: imagePreview
                  ? "var(--trophy-yellow)"
                  : "var(--main-purple)",
                cursor: "pointer",
              }}
            ></ion-icon>
            <button
              type="submit"
              className="btn fw-bold rounded-3"
              disabled={isSubmitting}
              style={{
                backgroundColor: "var(--main-purple)",
                padding: ".3rem 4rem",
                border: "none",
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? "Posting..." : "Update"}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
